import React, {useEffect, useState} from 'react';
import LinkWidget from './LinkWidget';
import ContactTypeFilter from './ContactTypeFilter';
import {NodeView} from "../../../../types/Node";
import {Button, Card, Spinner, Stack} from "react-bootstrap";
import {StringUtil} from "zavadil-ts-common";
import SelectRegion from "../../../../components/SelectRegion";
import {RegionView} from "../../../../types/Region";
import {useRestClient} from "../../../../client/SpmRestClient";
import {useAlerts} from "../../../../utils/UserAlerts";

interface TestFlightProps {
	node?: NodeView | null;
	onNodeSelected: (node: NodeView) => void;
	onSelectParent: () => void;
	onJumpToNodeRequested: (id?: number) => void;
}

export default function TestFlight({node, onNodeSelected, onSelectParent, onJumpToNodeRequested}: TestFlightProps) {
	const restClient = useRestClient();
	const userAlerts = useAlerts();
	const [regionId, setRegionId] = useState<number | null>();
	const [region, setRegion] = useState<RegionView>();
	const [children, setChildren] = useState<Array<NodeView>>();

	useEffect(
		() => {
			if (!regionId) {
				setRegion(undefined);
				return;
			}
			restClient.regions
				.load(regionId)
				.then(setRegion)
				.catch((e) => userAlerts.err(e));
		},
		[regionId]
	);

	useEffect(
		() => {
			setChildren(undefined);
			if (!node) {
				restClient.nodes
					.roots()
					.then(
						(roots) => {
							if (roots.length === 1) {
								onNodeSelected(roots[0]);
								return;
							}
							setChildren(roots);
						}
					)
					.catch((e) => userAlerts.err(e));
				return;
			}
			if (!node.id) return;
			restClient.nodes
				.children(node.id)
				.then(setChildren)
				.catch((e) => userAlerts.err(e));
		},
		[node]
	);

	return (
		<div className="test-flight">
			<Card className="border-dark fake-phone">
				<Card.Header className="text-white bg-dark">SPM</Card.Header>
				<Card.Body className="fake-display">
					<Stack gap={2}>
						{
							node && node.text && <div
								dangerouslySetInnerHTML={{
									__html: node.text.replace(/\n/gi, '<br/>')
								}}
							/>
						}
						{
							node && StringUtil.notBlank(node?.textSecondary) && <div
								className="font-italic"
								dangerouslySetInnerHTML={{
									__html: node.textSecondary.replace(/\n/gi, '<br/>')
								}}
							/>
						}
						<div className="text-center">
							{
								children ? children.map(
									(child) => <p key={child.id}>
										<Button
											variant={child.isEnabled ? "dark" : "secondary"}
											onClick={() => onNodeSelected(child)}
										>{child.answer}</Button>
										{
											child.answerSecondary && child.answerSecondary.length > 0 && (
												<div className="font-italic">{child.answerSecondary}</div>
											)
										}
									</p>
								) : <Spinner/>
							}
						</div>
						{
							node && node.isContact && <div>
								<h4>Vaše poloha</h4>
								<div>Nepodařilo se zjistit Vaši polohu. Zapněte si GPS nebo zvolte okres ručně.</div>
								<SelectRegion
									regionId={regionId}
									onChange={setRegionId}
								/>
							</div>
						}
						{
							node && node.isContact && node.contactTypeFilters.length > 0 && <div>
								<h4>Kontakty</h4>
								{
									regionId &&
									node.contactTypeFilters.map(
										(filter, index) => <ContactTypeFilter
											key={index}
											filter={filter}
											regionId={regionId}
										/>
									)
								}
							</div>
						}
						{
							node && node.isContact && node.links.length > 0 && <div>
								<h4>Užitečné odkazy</h4>
								{
									node.links.map(
										(link, index) => <LinkWidget
											key={index}
											url={link.url}
											name={link.name}
											description={link.description}
											onJumpToNodeRequested={onJumpToNodeRequested}
										/>
									)
								}
							</div>
						}
						{
							region &&
							node &&
							node.isContact &&
							node.googleSearch &&
							node.googleSearch.length > 0 && <div>
								<h4>Vyhledat na Google</h4>
								<LinkWidget
									url={`https://www.google.com/search?q=${node.googleSearch} ${region.name}`}
									name="Vyhledat na Google"
									label="Vyhledat"
									description={`Zkuste na Google vyhledat výraz "${node.googleSearch} ${region.name}".`}
									onJumpToNodeRequested={onJumpToNodeRequested}
								/>
							</div>
						}
					</Stack>
				</Card.Body>
				<Card.Footer className="text-white bg-dark">
					<Stack direction="horizontal" gap={2} className="justify-content-center">
						<Button
							variant="dark"
							className="border-light"
							onClick={onSelectParent}
						>Zpět</Button>
						<Button
							variant="dark"
							className="border-light"
							onClick={() => onJumpToNodeRequested()}
						>Reset</Button>
					</Stack>
				</Card.Footer>
			</Card>
		</div>
	);
}
